import { formatDiscountBadge } from '../utils/discountMath';

const formatAmount = (amount) => Number(amount || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export const QuotePricingTable = ({ quote }) => {
  const currency = quote.currency || 'CI$';
  const money = (amount) => `${currency} ${formatAmount(amount)}`;
  const items = quote.items || [];
  // Saved line totals already include their discounts; never subtract again.
  const lineTotal = (item) => item.total ?? Number(item.unit_price || 0) * Number(item.quantity ?? 1);
  const subtotal = quote.subtotal ?? items.reduce((sum, item) => sum + Number(lineTotal(item)), 0);
  const hasDiscount = ['percent', 'fixed'].includes(quote.discount_type) && Number(quote.discount_amount) > 0;
  const discountLabel = `Discount${quote.discount_type === 'percent' ? ` (${Number(quote.discount_value || 0)}%)` : ''}${quote.discount_label ? ` — ${quote.discount_label}` : ''}`;
  const cell = 'px-2 sm:px-4 py-2.5 break-words';

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-emerald-100 overflow-hidden" data-testid="quote-pricing">
      <table className="w-full table-fixed text-xs sm:text-sm" aria-label="Quote pricing breakdown">
        <colgroup><col className="w-[42%]" /><col className="w-[10%]" /><col className="w-[24%]" /><col className="w-[24%]" /></colgroup>
        <thead className="bg-emerald-50 text-emerald-800">
          <tr>
            <th scope="col" className={`${cell} text-left font-semibold`}>Item</th>
            <th scope="col" className="px-1 py-2.5 text-right font-semibold">Qty</th>
            <th scope="col" className={`${cell} text-right font-semibold`}>Unit</th>
            <th scope="col" className={`${cell} text-right font-semibold`}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const badge = formatDiscountBadge(item, currency);
            const gross = Number(item.unit_price || 0) * Number(item.quantity ?? 1);
            const discount = Math.max(Math.round((gross - Number(lineTotal(item))) * 100) / 100, 0);
            return (
              <tr key={index} className="border-t border-gray-100" data-testid={`quote-line-${index}`}>
                <td className={`${cell} text-gray-800 [overflow-wrap:anywhere]`}>
                  <div data-testid={`quote-line-description-${index}`}>{item.description || item.name}</div>
                  {badge && <div className="mt-1 text-xs text-emerald-700" data-testid={`quote-line-discount-${index}`}>
                    {badge}{item.discount_label ? ` — ${item.discount_label}` : ''}
                    <span className="block">−{money(discount)}</span>
                  </div>}
                </td>
                <td className="px-1 py-2.5 text-right text-gray-600" data-testid={`quote-line-quantity-${index}`}>{item.quantity ?? 1}</td>
                <td className={`${cell} text-right text-gray-600 [overflow-wrap:anywhere]`} data-testid={`quote-line-unit-${index}`}>{money(item.unit_price)}</td>
                <td className={`${cell} text-right text-gray-900 font-medium [overflow-wrap:anywhere]`} data-testid={`quote-line-total-${index}`}>{money(lineTotal(item))}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-200" data-testid="quote-subtotal-row">
            <th scope="row" colSpan={3} className={`${cell} text-right text-gray-600 font-normal`}>Subtotal</th>
            <td className={`${cell} text-right [overflow-wrap:anywhere]`} data-testid="quote-subtotal">{money(subtotal)}</td>
          </tr>
          {hasDiscount && <tr className="text-emerald-700" data-testid="quote-discount-row">
            <th scope="row" colSpan={3} className={`${cell} text-right font-medium`} data-testid="quote-discount-label">{discountLabel}</th>
            <td className={`${cell} text-right font-medium [overflow-wrap:anywhere]`} data-testid="quote-discount-amount">−{money(quote.discount_amount)}</td>
          </tr>}
          {Number(quote.tax) !== 0 && quote.tax != null && <tr data-testid="quote-tax-row">
            <th scope="row" colSpan={3} className={`${cell} text-right text-gray-600 font-normal`}>Tax</th>
            <td className={`${cell} text-right [overflow-wrap:anywhere]`} data-testid="quote-tax">{money(quote.tax)}</td>
          </tr>}
          <tr className="border-t border-emerald-200 bg-emerald-50/50" data-testid="quote-total-row">
            <th scope="row" colSpan={3} className={`${cell} text-right font-bold text-gray-900`}>Total</th>
            <td className={`${cell} text-right font-bold text-emerald-700 sm:text-lg [overflow-wrap:anywhere]`} data-testid="quote-total">{money(quote.total)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
};